import type { Baby } from "../../lib/queries.ts";
import { cn } from "../../lib/utils.ts";

/**
 * A dashboard section: a serif head over a hairline, with an optional note.
 * The report nests its sections under each baby's name, a level down.
 * Settings groups its kicker sections under these, with an `id` so other
 * pages can link to a group (`/settings#device`).
 */
export function Section({
  title,
  note,
  children,
  className,
  level = 2,
  id,
}: {
  title: string;
  note?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  level?: 2 | 3;
  id?: string;
}) {
  const Heading = level === 3 ? "h3" : "h2";
  return (
    <section id={id} className={cn("mt-10", className)} aria-label={title}>
      <div className="flex break-after-avoid flex-wrap items-baseline gap-x-3 border-b border-ink pb-1.5">
        <Heading className="heading text-[22px]">{title}</Heading>
        {note ? <p className="ml-auto text-[12px] opacity-60">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * One column per baby on a tablet (twins side by side, same scales), stacked
 * below `md`. On a phone the overview's baby tabs pass the selected baby
 * alone. A single baby needs no name over its column.
 *
 * A lone column keeps one key, so a tab switch swaps the baby under the same
 * charts: an open table stays open, a tapped day stays tapped, and the bars
 * change in place, so the page never jumps.
 */
export function PerBaby({
  babies,
  children,
}: {
  babies: Baby[];
  children: (baby: Baby) => React.ReactNode;
}) {
  return (
    <div className={cn("mt-4 grid gap-x-10 gap-y-8", babies.length > 1 && "md:grid-cols-2")}>
      {babies.map((b) => (
        <div key={babies.length > 1 ? b.id : "one"} className="min-w-0">
          {babies.length > 1 ? <h3 className="heading mb-2 text-[18px]">{b.name}</h3> : null}
          {children(b)}
        </div>
      ))}
    </div>
  );
}
